import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/api/client";

const Ctx = createContext(null);

const DEFAULTS = {
  app_name_ar: "وكالة التأشيرات للسفر والسياحة",
  app_name_en: "Travel & Tourism Visa Agency",
  short_name: "VTA",
  tagline_ar: "منصة تشغيل وكالة التأشيرات",
  tagline_en: "Visa Agency Operations Platform",
  logo_url: "/logo.png",
  theme_color: "#0a0a0a",
  background_color: "#0a0a0a",
  accent_color: "#d4af37",
  footer_text_ar: `© ${new Date().getFullYear()} جميع الحقوق محفوظة لوكالة التأشيرات للسفر والسياحة`,
  footer_text_en: `© ${new Date().getFullYear()} All rights reserved — Travel & Tourism Visa Agency`,
  // Identity credentials (rendered in the footer once STAS fills them)
  tourism_license_number: "",
  commercial_register: "",
  // Contact channels (rendered in the footer once STAS fills them)
  contact_phone: "",
  contact_email: "",
  whatsapp_number: "",
  location_url: "",
};

// Strictly neutral identity for anonymous candidate paths (/apply, /assessment).
// Nothing here may reveal the agency. Footer/AppShell components that read
// from the branding context will see ONLY these values.
const NEUTRAL = {
  app_name_ar: "",
  app_name_en: "Training Sandbox",
  short_name: "",
  tagline_ar: "",
  tagline_en: "",
  logo_url: "",
  theme_color: "#0a0a0a",
  background_color: "#0a0a0a",
  accent_color: "#d4af37",
  footer_text_ar: "",
  footer_text_en: "",
  tourism_license_number: "",
  commercial_register: "",
  contact_phone: "",
  contact_email: "",
  whatsapp_number: "",
  location_url: "",
};

// True when the current page is part of the public, anonymous candidate
// experience. Computed once on import — these routes never change identity
// mid-session, so we don't need to react to it.
function isCandidatePath() {
  if (typeof window === "undefined" || !window.location) return false;
  const p = window.location.pathname || "";
  return p.startsWith("/assessment") || p.startsWith("/apply");
}

/**
 * Resolve a logo path to an absolute URL that works for both:
 *  - bundled assets (e.g. /logo.png in /public)
 *  - uploaded assets served from the backend (/api/static/branding/...)
 */
export function resolveLogo(url) {
  if (!url) return "/logo.png";
  if (/^https?:\/\//.test(url)) return url;
  if (url.startsWith("/api/")) {
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  }
  return url;
}

export function BrandingProvider({ children }) {
  const ANON = isCandidatePath();
  // On candidate paths, freeze to NEUTRAL and never fetch from /branding.
  const [branding, setBranding] = useState(ANON ? NEUTRAL : DEFAULTS);
  const [loaded, setLoaded] = useState(ANON);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(async () => {
    if (ANON) {
      // Candidate paths must NEVER see the agency identity. Wipe any
      // previously-cached global and exit early.
      if (typeof window !== "undefined") {
        window.__VTA_BRANDING__ = NEUTRAL;
        setTick((n) => n + 1);
      }
      setLoaded(true);
      return;
    }
    try {
      const { data } = await api.get("/branding");
      // Force the visual identity colours back to the locked defaults so
      // STAS can never repaint the workspace. Logo, names, taglines and
      // footer text remain editable.
      const merged = {
        ...DEFAULTS,
        ...data,
        theme_color: DEFAULTS.theme_color,
        background_color: DEFAULTS.background_color,
        accent_color: DEFAULTS.accent_color,
      };
      setBranding(merged);
      if (typeof window !== "undefined") {
        window.__VTA_BRANDING__ = merged;
        // CSS variables locked to the identity palette.
        const root = document.documentElement;
        root.style.setProperty("--vta-gold", DEFAULTS.accent_color);
        root.style.setProperty("--vta-bg", DEFAULTS.theme_color);
        root.style.setProperty("--vta-surface", DEFAULTS.background_color);
        setTick((n) => n + 1);
      }
    } catch {
      setBranding(DEFAULTS);
    } finally {
      setLoaded(true);
    }
  }, [ANON]);

  useEffect(() => { refresh(); }, [refresh]);

  // Reactive favicon + document title when branding changes — but skip the
  // anonymous Visa Operations Assessment pages so they never leak the agency
  // identity through the browser tab, favicon, or PWA install prompt.
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (ANON) {
      document.title = "Training Sandbox";
      const icon = document.querySelector("link[rel='icon']");
      if (icon) icon.setAttribute("href", "data:,");  // empty favicon
      const apple = document.querySelector("link[rel='apple-touch-icon']");
      if (apple) apple.setAttribute("href", "data:,");
      // Detach the PWA manifest so "Add to Home Screen" can't reveal the
      // agency name on Android / iOS.
      const manifest = document.querySelector("link[rel='manifest']");
      if (manifest) manifest.parentNode && manifest.parentNode.removeChild(manifest);
      // Wipe any meta tags that could leak branding.
      const wipe = (sel) => {
        const el = document.querySelector(sel);
        if (el) el.setAttribute("content", "Training Sandbox");
      };
      wipe('meta[name="application-name"]');
      wipe('meta[name="apple-mobile-web-app-title"]');
      const desc = document.querySelector('meta[name="description"]');
      if (desc) desc.setAttribute("content", "Candidate training sandbox");
      return;
    }
    document.title = branding.app_name_ar || DEFAULTS.app_name_ar;
    const icon = document.querySelector("link[rel='icon']");
    if (icon) icon.setAttribute("href", resolveLogo(branding.logo_url));
    const apple = document.querySelector("link[rel='apple-touch-icon']");
    if (apple) apple.setAttribute("href", resolveLogo(branding.logo_url));
  }, [branding, ANON]);

  const value = useMemo(
    () => ({ branding, loaded, refresh, tick }),
    [branding, loaded, refresh, tick],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useBranding = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error("useBranding must be inside BrandingProvider");
  return v;
};
